import { motion, AnimatePresence } from 'framer-motion';
import { ThumbsUpIcon } from '../activity-details/icons';
import {
  EXPERT_TIPS_COLORS,
  EXPERT_TIPS_SHADOWS,
  EXPERT_TIPS_RADIUS,
  EXPERT_TIPS_RESPONSIVE,
  EXPERT_TIPS_TYPOGRAPHY,
} from './constants';
import TipExpandedContent from './TipExpandedContent';
import type { ExpertTip, FeedbackType } from '../../types/expert-tip.types';

import ArrowDownIcon from '../../../assets/icons/arrow-down.svg';

interface TipCardProps {
  tip: ExpertTip;
  isExpanded: boolean;
  onToggle: () => void;
  onFeedbackSubmit: (tipId: number, feedbackType: FeedbackType) => Promise<void>;
  isFeedbackSubmitting: boolean;
}

export default function TipCard({
  tip,
  isExpanded,
  onToggle,
  onFeedbackSubmit,
  isFeedbackSubmitting,
}: TipCardProps) {
  return (
    <div
      style={{
        backgroundColor: EXPERT_TIPS_COLORS.cardBackground,
        borderRadius: `${EXPERT_TIPS_RADIUS.card}px`,
        boxShadow: EXPERT_TIPS_SHADOWS.card,
      }}
    >
      {/* Collapsed header — always visible, acts as toggle button */}
      <button
        onClick={onToggle}
        className={`w-full text-left flex items-center ${EXPERT_TIPS_RESPONSIVE.cardPaddingClasses}`}
        style={{ gap: '10px' }}
      >
        {/* Content area: title + like count */}
        <div className="flex-1 flex flex-col" style={{ gap: '8px' }}>
          <span
            className={`font-normal ${EXPERT_TIPS_TYPOGRAPHY.tipTitle}`}
            style={{
              fontFamily: 'DM Sans, sans-serif',
              color: EXPERT_TIPS_COLORS.textPrimary,
              lineHeight: 1.4,
            }}
          >
            {tip.title}
          </span>

          {tip.statistics.thumbsUpCount > 0 && (
            <div
              className="flex items-center"
              style={{
                gap: '4px',
                padding: '4px 8px',
                borderRadius: `${EXPERT_TIPS_RADIUS.likeBadge}px`,
              }}
            >
              <ThumbsUpIcon size={14} color={EXPERT_TIPS_COLORS.textPrimary} />
              <span
                className={`font-medium ${EXPERT_TIPS_TYPOGRAPHY.likeCount}`}
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  color: EXPERT_TIPS_COLORS.textPrimary,
                }}
              >
                {tip.statistics.thumbsUpCount}
              </span>
            </div>
          )}
        </div>

        {/* Arrow icon — rotates on expand */}
        <div className="flex-shrink-0 flex items-center justify-center w-12 h-12">
          <motion.img
            src={ArrowDownIcon}
            alt=""
            className={EXPERT_TIPS_RESPONSIVE.arrowSizeClasses}
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          />
        </div>
      </button>

      {/* Expanded content — animated enter/exit */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="expanded-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.3, ease: 'easeOut' },
              opacity: { duration: 0.2, ease: 'easeOut', delay: 0.1 },
            }}
            style={{ overflow: 'hidden' }}
          >
            <TipExpandedContent
              tip={tip}
              onFeedbackSubmit={onFeedbackSubmit}
              isFeedbackSubmitting={isFeedbackSubmitting}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
